import clsx from 'clsx';
import { memo, ReactNode } from 'react';
import { Box, Flex } from '@optiaxiom/react';
import { Handle, Position, useConnection } from '@xyflow/react';

import { HANDLE_IDS } from '../../../utils/constants';

import {
  bottomSourceHandle,
  ExecutionStatus,
  leftCircle,
  leftCircleHandle,
  leftSourceHandle,
  nodeContainer,
  nodeContainerModifiers,
  rightCircle,
  rightCircleHandle,
  rightSourceHandle,
  targetHandle,
  targetHandleConnecting,
  targetHandleError,
  topSourceHandle,
  visuallyHidden,
} from './NodeContainer.css';

type NodeContainerProps = {
  children: ReactNode;
  selected?: boolean;
  hasErrors?: boolean;
  isSourceNode?: boolean;
  executionStatus?: ExecutionStatus;
  isExecutionSelected?: boolean;
  style?: React.CSSProperties;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  className?: string;
  dataAttributes?: Record<string, string | boolean>;
  nodeId?: string;
};

export const NodeContainer = memo(
  ({
    children,
    selected,
    hasErrors,
    isSourceNode,
    executionStatus,
    isExecutionSelected,
    style,
    onMouseEnter,
    onMouseLeave,
    className,
    dataAttributes,
    nodeId,
  }: NodeContainerProps) => {
    const connection = useConnection();
    const isTarget = connection.inProgress && connection.toNode?.id === nodeId;
    const isNonSource = connection.inProgress && connection.fromNode?.id !== nodeId;

    const isConnecting = connection.inProgress;
    const isValidConnection = connection.isValid;
    const showError = isTarget && !isValidConnection;

    const shouldShowSourceHandles = !isConnecting;
    const shouldShowTargetHandles = !isConnecting || isNonSource;

    // Convert dataAttributes to data-* props
    const dataProps = dataAttributes
      ? Object.entries(dataAttributes).reduce(
          (acc, [key, value]) => {
            acc[`data-${key}`] = String(value);
            return acc;
          },
          {} as Record<string, string>
        )
      : {};

    return (
      <Flex
        className={clsx(
          nodeContainer,
          selected && nodeContainerModifiers.selected,
          hasErrors && nodeContainerModifiers.error,
          isSourceNode && nodeContainerModifiers.source,
          executionStatus && nodeContainerModifiers[executionStatus],
          isExecutionSelected && nodeContainerModifiers.executionSelected,
          className
        )}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...dataProps}
      >
        {/* Source handles - one at each side, hidden during connection */}
        <>
          <Handle
            type="source"
            id={HANDLE_IDS.SOURCE_TOP}
            position={Position.Top}
            className={clsx(topSourceHandle, !shouldShowSourceHandles && visuallyHidden)}
            isConnectableEnd={false}
          />
          <Handle
            type="source"
            id={HANDLE_IDS.SOURCE_LEFT}
            position={Position.Left}
            className={clsx(leftSourceHandle, !shouldShowSourceHandles && visuallyHidden)}
            isConnectableEnd={false}
          />
          <Handle
            type="source"
            id={HANDLE_IDS.SOURCE_RIGHT}
            position={Position.Right}
            className={clsx(rightSourceHandle, !shouldShowSourceHandles && visuallyHidden)}
            isConnectableEnd={false}
          />
          <Handle
            type="source"
            id={HANDLE_IDS.SOURCE_BOTTOM}
            position={Position.Bottom}
            className={clsx(bottomSourceHandle, !shouldShowSourceHandles && visuallyHidden)}
            isConnectableEnd={false}
          />
        </>

        {/* Target handle - entire node body, shown when not connecting or when this is the target */}

        <Handle
          type="target"
          position={Position.Top}
          id={HANDLE_IDS.TARGET_BODY}
          className={clsx(
            targetHandle,
            !shouldShowTargetHandles && visuallyHidden,
            showError && targetHandleError,
            isTarget && !showError && targetHandleConnecting
          )}
          isConnectableStart={false}
        />

        {/* Connection circles on left and right edges */}
        <Box className={leftCircle}>
          <Handle
            type="source"
            id={HANDLE_IDS.SOURCE_LEFT_CIRCLE}
            position={Position.Left}
            className={clsx(leftCircleHandle, !shouldShowSourceHandles && visuallyHidden)}
            isConnectableEnd={false}
          />

          <Handle
            type="target"
            id={HANDLE_IDS.TARGET_LEFT_CIRCLE}
            position={Position.Left}
            className={clsx(
              leftCircleHandle,
              !shouldShowTargetHandles && visuallyHidden,
              showError && targetHandleError,
              isTarget && !showError && targetHandleConnecting
            )}
            isConnectableStart={false}
          />
        </Box>
        <Box className={rightCircle}>
          <Handle
            type="source"
            id={HANDLE_IDS.SOURCE_RIGHT_CIRCLE}
            position={Position.Right}
            className={clsx(rightCircleHandle, !shouldShowSourceHandles && visuallyHidden)}
            isConnectableEnd={false}
          />

          <Handle
            type="target"
            id={HANDLE_IDS.TARGET_RIGHT_CIRCLE}
            position={Position.Right}
            className={clsx(
              rightCircleHandle,
              !shouldShowTargetHandles && visuallyHidden,
              showError && targetHandleError,
              isTarget && !showError && targetHandleConnecting
            )}
            isConnectableStart={false}
          />
        </Box>
        {children}
      </Flex>
    );
  }
);

NodeContainer.displayName = 'NodeContainer';
